import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const StatBox = ({ icon, label, value, color, onClick }) => (
  <div
    className={`flex flex-col gap-1 p-4 rounded-lg bg-background border border-border ${onClick ? 'cursor-pointer hover:border-accent/50 transition-colors' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e?.key === 'Enter' || e?.key === ' ') { e?.preventDefault(); onClick(); } } : undefined}
  >
    <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-1 ${color}`}>
      <Icon name={icon} size={16} className="text-white" />
    </div>
    <span className="text-2xl font-bold text-foreground font-heading">{value ?? '—'}</span>
    <span className="text-xs text-muted-foreground">{label}</span>
  </div>
);

const CompanyOverviewPanel = ({ sites = [], openSnags = 0, expiringDocuments = 0, workers: propWorkers = 0 }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [tasksCompletedThisWeek, setTasksCompletedThisWeek] = useState(0);
  const [workerCount, setWorkerCount] = useState(propWorkers);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userProfile?.company_id) {
      fetchAdditionalStats(userProfile?.company_id);
    }
  }, [userProfile?.company_id]);

  useEffect(() => {
    setWorkerCount(propWorkers);
  }, [propWorkers]);

  const fetchAdditionalStats = async (companyId) => {
    try {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString();

      const [workersRes, tasksRes] = await Promise.all([
        // jyyb8AfJ — exclude admin-deactivated users from the admin
        // dashboard's "team size" summary stat. Active users only.
        supabase
          ?.from('user_profiles')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('company_id', companyId)
          ?.in('role', ['worker', 'site_manager'])
          ?.is('deactivated_at', null),
        supabase
          ?.from('daily_tasks')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('company_id', companyId)
          ?.eq('status', 'completed')
          ?.gte('completed_at', weekAgo)
      ]);

      if (workersRes?.count != null) setWorkerCount(workersRes?.count);
      if (tasksRes?.count != null) setTasksCompletedThisWeek(tasksRes?.count);
    } catch (err) {
      console.error('CompanyOverviewPanel fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      icon: 'Building2',
      label: 'Total Sites',
      value: sites?.length,
      color: 'bg-blue-500',
      onClick: () => navigate('/admin-dashboard'),
    },
    {
      icon: 'HardHat',
      label: 'Workers',
      value: workerCount,
      color: 'bg-teal-500',
      onClick: () => navigate('/workers'),
    },
    {
      icon: 'AlertCircle',
      label: 'Open Issues / Snags',
      value: openSnags,
      color: 'bg-amber-500',
      onClick: () => navigate('/snags'),
    },
    {
      icon: 'FileWarning',
      label: 'Docs Expiring Soon',
      value: expiringDocuments,
      color: 'bg-orange-500',
      onClick: () => navigate('/document-management'),
    },
    {
      icon: 'CheckCircle2',
      label: 'Tasks Completed (7d)',
      value: loading ? '…' : tasksCompletedThisWeek,
      color: 'bg-green-500',
      onClick: () => navigate('/task-completion-workflow'),
    },
  ];

  return (
    <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-5 md:p-6 mb-6 md:mb-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Icon name="LayoutDashboard" size={18} className="text-accent" />
        </div>
        <div>
          <h2 className="text-base md:text-lg font-semibold text-foreground font-heading">Company Overview</h2>
          <p className="text-xs text-muted-foreground">Live snapshot of your company activity</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {stats?.map((stat, i) => (
          <StatBox key={i} {...stat} />
        ))}
      </div>
    </div>
  );
};

export default CompanyOverviewPanel;
